import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';
import { equipmentRequestsNewService } from '../services/equipmentRequestsNew.service';
import { RequestsNewService } from '@/modules/inventory/requests/new/services/RequestsNew.service';

export function useEquipmentRequestsNew() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const userId = profile?.id || '';

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    projectId: '',
    neededFrom: '',
    neededUntil: '',
    priority: 'normal' as any,
    items: [] as any[],
  });

  const projectsQuery = useQuery({
    queryKey: ['projects', 'simple-list', companyId],
    queryFn: () => RequestsNewService.getProjects(companyId),
    enabled: !!companyId,
  });

  const categoriesQuery = useQuery({
    queryKey: ['equipment', 'categories', companyId],
    queryFn: () => equipmentRequestsNewService.getEquipmentCategories(companyId),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => equipmentRequestsNewService.createRequest(userId, { ...data, companyId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment', 'requests'] });
      toast.success('Đã gửi yêu cầu thiết bị');
      navigate('/equipment/requests');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Không thể gửi yêu cầu');
    }
  });

  const addItem = (item: any) => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { ...item, id: Math.random().toString(36).substring(7) }]
    }));
  };

  const removeItem = (id: string) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(i => i.id !== id)
    }));
  };

  return {
    formData,
    projects: projectsQuery.data || [],
    categories: categoriesQuery.data || [],
    isLoading: projectsQuery.isLoading || categoriesQuery.isLoading,
    isSaving: createMutation.isPending,
    setFormData,
    addItem,
    removeItem,
    createRequest: () => createMutation.mutate(formData),
  };
}
